import { useCallback, useEffect, useRef, useState } from 'react'
import Chart from '../analysis/charts/Chart'
import FaultsTable from '../analysis/FaultsTable'
import ConfigChangesTable from '../analysis/ConfigChangesTable'
import StatisticsDisplay from '../analysis/StatisticsDisplay'
import { useHistoricalData } from '../../hooks/useHistoricalData'
import { useTimeRangeManager } from '../../hooks/useTimeRangeManager'
import LoadingSpinner from '../ui/LoadingSpinner'
import { useConfigSnapshots } from '../../hooks/useConfigSnapshots'
import { useDeviceMeasurementManagement } from '../../hooks/useDeviceMeasurementManagement'
import { useChartOptions } from '../../hooks/useChartOptions'
import { useRelatedDataNavigation } from '../../hooks/useRelatedDataNavigation'
import { Alert, Card, PageContainer, PageHeader, StatusIndicator } from '../common'
import '../../styles/animations.css'

function AnalysisPageHeader({ loading, error, hasData }) {
  // Status logic for analysis page
  const getAnalysisStatus = () => {
    if (loading) {
      return { status: 'loading', text: 'Loading analysis data...', animation: 'pulse' }
    }
    if (error) {
      return { status: 'error', text: 'Data loading failed', animation: 'none' }
    }
    if (hasData) {
      return { status: 'success', text: 'Analysis ready', animation: 'none' }
    }
    return { status: 'warning', text: 'Select data to analyze', animation: 'none' }
  }

  const statusConfig = getAnalysisStatus()

  return (
    <PageHeader 
      title="Analysis"
      subtitle="Data analysis tools for your Revatek Altion regulator."
      statusIndicator={
        <StatusIndicator 
          status={statusConfig.status}
          text={statusConfig.text}
          animation={statusConfig.animation}
        />
      }
    />
  )
}

function AnalysisPage() {
  const [activeTab, setActiveTab] = useState('stats') // 'stats', 'faults', or 'configChanges'
  const [exportCsvLoading, setExportCsvLoading] = useState(false)

  // Using window.location.search for one-time init of device/measurement, as useSearchParams is in useChartOptions.
  const searchParamsForDeviceInit = new URLSearchParams(window.location.search)
  const initialDeviceFromUrl = searchParamsForDeviceInit.get('device')
  const initialMeasurementFromUrl = searchParamsForDeviceInit.get('measurement')

  const {
    selectedDevices,
    setSelectedDevices,
    selectedMeasurements,
    setSelectedMeasurements,
    availableDevices,
    deviceMeasurementsMap,
    devicesLoading,
    devicesError,
    availableMeasurements,
    measurementsLoading,
  } = useDeviceMeasurementManagement(initialDeviceFromUrl, initialMeasurementFromUrl)

  const { options: chartOptions, dispatch: dispatchChartOptions } = useChartOptions()
  const {
    timeRange,
  } = chartOptions

  const chartAreaRef = useRef(null)

  const {
    handleViewRelatedData,
    isExternallySetCustom,
    handleExternalCustomProcessed,
  } = useRelatedDataNavigation({
    setSelectedDevices,
    setSelectedMeasurements,
    dispatchChartOptions,
    chartAreaRef,
    selectedDevices,
    availableMeasurements,
  })

  const { configSnapshots, configLoading, configError } = useConfigSnapshots(timeRange, true)

  const {
    data, faults, loading: historicalDataLoading, faultsLoading, error,
    statsData, series, annotations, loadData,
  } = useHistoricalData(
    selectedDevices,
    selectedMeasurements,
    timeRange,
    true, // showFaults
    true, // showStats
    true, // showConfigChanges
    configSnapshots,
  )

  const handleExportData = useCallback(async () => {
    if (!data?.length || !selectedDevices?.length || !selectedMeasurements?.length) {
      alert('No data available to export.')
      return
    }
    setExportCsvLoading(true)
    try {
      const rowsByTimestamp = {}
      const columns = new Set()
      data.forEach(row => {
        const t = row._time || row.timestamp
        if (!t) return
        const d = row.device, m = row._field || row.measurement || 'temp'
        const v = row._value ?? row.value
        if (!d || !m || v == null || v === '') return
        const k = `${d}_${m}`;
        (rowsByTimestamp[t] ??= {})[k] = v
        columns.add(k)
      })
      const colArr = [...columns]
      const headers = ['Timestamp', ...colArr]
      const pad = n => n.toString().padStart(2, '0')
      const format = ts => {
        const d = new Date(ts)
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
      }
      const csvRows = [headers.join(',')]
      Object.entries(rowsByTimestamp).forEach(([ts, vals]) => {
        csvRows.push([format(ts), ...colArr.map(k => vals[k] ?? '')].join(','))
      })
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        link.href = URL.createObjectURL(blob)
        link.download = `export_${selectedDevices.join('_')}_${selectedMeasurements.join('_')}_${new Date().toISOString().split('T')[0]}.csv`
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      } else {
        alert('CSV export is not supported in your browser.')
      }
    } catch (exportError) {
      console.error('Error exporting CSV:', exportError)
      alert('An error occurred while exporting data.')
    } finally {
      setExportCsvLoading(false)
    }
  }, [data, selectedDevices, selectedMeasurements])


  useEffect(() => {
    if (selectedDevices.length > 0 && selectedMeasurements.length > 0) {
      loadData(selectedDevices, selectedMeasurements, timeRange, true, true)
    }
  }, [selectedDevices, selectedMeasurements, timeRange, loadData])

  const {handleTimeRangeChange} = useTimeRangeManager(timeRange, updatedTimeRange => {
    dispatchChartOptions({ type: 'SET_TIME_RANGE', payload: updatedTimeRange })
  })

  const handleDevicesChange = useCallback(newDevices => {
    setSelectedDevices(newDevices)
  }, [setSelectedDevices])

  const handleMeasurementsChange = useCallback(newMeasurements => {
    setSelectedMeasurements(newMeasurements)
  }, [setSelectedMeasurements])

  const handleClearSelections = useCallback(() => {
    setSelectedDevices([])
    setSelectedMeasurements([])
    handleTimeRangeChange('allTime')
  }, [setSelectedDevices, setSelectedMeasurements, handleTimeRangeChange])
  
  // Show loading state while devices are being loaded
  if (devicesLoading) {
    return (
      <PageContainer>
        <PageHeader 
          title="Analysis"
          subtitle="Data analysis tools for your Revatek Altion regulator."
          statusIndicator={
            <StatusIndicator 
              status="loading"
              text="Loading devices..."
              animation="pulse"
            />
          }
        />
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Initializing analysis tools...</p>
        </div>
      </PageContainer>
    )
  }

  if (devicesError) {
    return (
      <PageContainer>
        <AnalysisPageHeader />
        <Alert 
          type="danger"
          title="Device Loading Error"
          message={devicesError}
        />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <AnalysisPageHeader 
        loading={historicalDataLoading}
        error={error}
        hasData={data?.length > 0}
      />
      
      {/* Empty State - When no devices selected */}
      {(!selectedDevices || selectedDevices.length === 0) && !devicesLoading && (
        <Alert 
          type="info" 
          title="Welcome to Analysis"
          message="Select devices and measurements from the chart controls below to begin analyzing your regulator data."
        />
      )}
      
      {/* Chart and Controls Card */}
      <Card 
        title={
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <i className="bi bi-graph-up me-2"></i>
              Chart & Controls
            </span>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handleExportData}
              disabled={
                exportCsvLoading ||
                historicalDataLoading ||
                !data?.length ||
                !selectedDevices?.length ||
                !selectedMeasurements?.length
              }
            >
              {exportCsvLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <><i className="bi bi-download me-2"></i>Download CSV</>
              )}
            </button>
          </div>
        }
        shadow={true}
        className="mb-4"
      >
        <Chart
          chartAreaRef={chartAreaRef}
          selectedDevices={selectedDevices}
          selectedMeasurements={selectedMeasurements}
          data={data}
          series={series}
          annotations={annotations}
          chartDataLoading={historicalDataLoading}
          statsData={statsData}
          error={error}
          handleDevicesChange={handleDevicesChange}
          availableDevices={availableDevices}
          deviceMeasurementsMap={deviceMeasurementsMap}
          devicesLoading={devicesLoading}
          devicesError={devicesError}
          handleMeasurementsChange={handleMeasurementsChange}
          availableMeasurements={availableMeasurements}
          measurementsLoading={measurementsLoading}
          handleClearSelections={handleClearSelections}
          timeRange={timeRange}
          onTimeRangeChange={handleTimeRangeChange}
          isExternallySetCustom={isExternallySetCustom}
          onExternalCustomProcessed={handleExternalCustomProcessed}
          configLoading={configLoading}
        />
      </Card>

      {/* Analysis Tabs Card - Only show when data is available */}
      {(selectedDevices?.length > 0 || faults?.length > 0 || configSnapshots?.length > 0) && (
        <Card 
          title={
            <span>
              <i className="bi bi-pie-chart me-2"></i>
              Analysis Results
            </span>
          }
          shadow={true}
          className="mb-4"
        >
        <ul className="nav nav-tabs" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => setActiveTab('stats')}
              role="tab"
              aria-selected={activeTab === 'stats'}
              aria-controls="stats-panel"
              id="stats-tab"
            >
              <i className="bi bi-bar-chart-line-fill me-1"></i>
              Statistics
              {statsData && Object.keys(statsData).length > 0 && (
                <span className="badge text-bg-secondary ms-2">{Object.keys(statsData).length}</span>
              )}
            </button>
          </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'faults' ? 'active' : ''}`}
            onClick={() => setActiveTab('faults')}
            role="tab"
            aria-selected={activeTab === 'faults'}
            aria-controls="faults-panel"
            id="faults-tab"
          >
            <i className="bi bi-exclamation-triangle-fill me-1"></i>
            Faults
            {faults && faults.length > 0 && (
              <span className="badge text-bg-secondary ms-2">{faults.length}</span>
            )}
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'configChanges' ? 'active' : ''}`}
            onClick={() => setActiveTab('configChanges')}
            role="tab"
            aria-selected={activeTab === 'configChanges'}
            aria-controls="config-changes-panel"
            id="config-changes-tab"
          >
            <i className="bi bi-gear-fill me-1"></i>
            Configuration Changes
            {configSnapshots && configSnapshots.length > 0 && (
              <span className="badge text-bg-secondary ms-2">{configSnapshots.length}</span>
            )}
          </button>
        </li>
      </ul>        <div className="tab-content mt-4">
          {activeTab === 'stats' && (
            <div
              role="tabpanel"
              id="stats-panel"
              aria-labelledby="stats-tab"
              className="fade show active"
            >
              <StatisticsDisplay statsData={statsData} />
            </div>
          )}

          {activeTab === 'faults' && (
            <div
              role="tabpanel"
              id="faults-panel"
              aria-labelledby="faults-tab"
              className="fade show active"
            >
              <>
                {faultsLoading && (
                  <div className="text-center my-4">
                    <LoadingSpinner message="Loading faults..." />
                  </div>
                )}
                {!faultsLoading && faults.length > 0 && (
                  <FaultsTable faults={faults} onViewRelatedData={handleViewRelatedData} />
                )}
                {!faultsLoading && faults.length === 0 && (
                  <Alert type="info" message="No faults found in selected time range." />
                )}
              </>
            </div>
          )}

          {activeTab === 'configChanges' && (
            <div
              role="tabpanel"
              id="config-changes-panel"
              aria-labelledby="config-changes-tab"
              className="fade show active"
            >
              <>
                {configLoading && (
                  <div className="text-center my-4">
                    <LoadingSpinner message="Loading configuration changes..." />
                  </div>
                )}
                {configError && <Alert type="danger" message={configError} />}
                {!configLoading && !configError && configSnapshots.length > 0 && (
                  <ConfigChangesTable configs={configSnapshots} onViewRelatedData={handleViewRelatedData} />
                )}
                {!configLoading && !configError && configSnapshots.length === 0 && (
                  <Alert type="info" message="No configuration changes found in selected time range." />
                )}
              </>
            </div>
          )}        </div>
        </Card>
      )}
    </PageContainer>
  )
}

export default AnalysisPage
